import React, { useState, useEffect } from 'react';
import { Search, Loader2, CheckCircle, Clock, XCircle, Eye, Filter } from 'lucide-react';
import { supabase } from '../../services/supabaseClient';
import { JobRequest, JobStatus } from '../../types';

export const AdminJobs: React.FC = () => {
    const [jobs, setJobs] = useState<JobRequest[]>([]);
    const [filteredJobs, setFilteredJobs] = useState<JobRequest[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<JobStatus | 'ALL'>('ALL');

    useEffect(() => {
        fetchAllJobs();

        // Real-time subscription
        const channel = supabase
            .channel('admin_jobs')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs' }, () => {
                fetchAllJobs();
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, []);

    useEffect(() => {
        let filtered = jobs;

        // Status filter
        if (statusFilter !== 'ALL') {
            filtered = filtered.filter(job => job.status === statusFilter);
        }

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(job =>
                job.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.workerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.customerId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.workerId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.skill?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setFilteredJobs(filtered);
    }, [jobs, searchTerm, statusFilter]);

    const fetchAllJobs = async () => {
        setIsLoading(true);
        try {
            const twelveMonthsAgo = new Date();
            twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);

            const { data, error } = await supabase
                .from('jobs')
                .select('*')
                .gte('created_at', twelveMonthsAgo.toISOString())
                .order('created_at', { ascending: false });

            if (error) throw error;

            const mappedJobs: JobRequest[] = (data || []).map((j: any) => ({
                id: j.id,
                customerId: j.customer_id,
                workerId: j.worker_id,
                customerName: j.customer_name,
                customerPhone: j.customer_phone,
                customerAvatar: j.customer_avatar,
                workerName: j.worker_name,
                workerPhone: j.worker_phone,
                workerAvatar: j.worker_avatar,
                skill: j.skill,
                title: j.title,
                description: j.description,
                status: j.status,
                price: j.price,
                date: j.date,
                otp: j.otp,
                completionOtp: j.completion_otp,
                isPaid: j.is_paid,
                paymentMethod: j.payment_method,
                platformFee: j.platform_fee,
                workerEarnings: j.worker_earnings,
                createdAt: j.created_at,
                isBroadcast: j.is_broadcast,
                mediaUrls: j.media_urls,
                voiceNoteUrl: j.voice_note_url,
                completionRequested: j.completion_requested,
                completionMediaUrls: j.completion_media_urls,
                userReview: j.user_review,
                workAddress: j.work_address,
                coordinates: j.coordinates
            }));

            setJobs(mappedJobs);
        } catch (error) {
            console.error('Error fetching jobs:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusBadge = (status: JobStatus) => {
        const styles: Record<JobStatus, string> = {
            'OPEN': 'bg-blue-50 text-blue-600 border-blue-100',
            'PENDING': 'bg-yellow-50 text-yellow-600 border-yellow-100',
            'ACCEPTED': 'bg-purple-50 text-purple-600 border-purple-100',
            'OTP_VERIFIED': 'bg-indigo-50 text-indigo-600 border-indigo-100',
            'IN_PROGRESS': 'bg-orange-50 text-orange-600 border-orange-100',
            'COMPLETED': 'bg-green-50 text-green-600 border-green-100',
            'CANCELLED': 'bg-red-50 text-red-600 border-red-100'
        };

        return (
            <span className={`px-2.5 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border ${styles[status]}`}>
                {status.replace('_', ' ')}
            </span>
        );
    };

    const completedJobsCount = jobs.filter(j => j.status === 'COMPLETED').length;

    return (
        <div className="space-y-6">
            {/* Header Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Total Jobs</p>
                    <p className="text-3xl font-black text-slate-900">{jobs.length}</p>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Completed</p>
                    <p className="text-3xl font-black text-green-600">{completedJobsCount}</p>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">In Progress</p>
                    <p className="text-3xl font-black text-orange-600">
                        {jobs.filter(j => ['ACCEPTED', 'IN_PROGRESS', 'OTP_VERIFIED'].includes(j.status)).length}
                    </p>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Cancelled</p>
                    <p className="text-3xl font-black text-red-600">
                        {jobs.filter(j => j.status === 'CANCELLED').length}
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            placeholder="Search by Job ID, Customer, Worker, Skill..."
                            className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 font-medium"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as JobStatus | 'ALL')}
                        className="px-4 py-3 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-blue-500 bg-white"
                    >
                        <option value="ALL">All Statuses</option>
                        <option value="OPEN">Open</option>
                        <option value="PENDING">Pending</option>
                        <option value="ACCEPTED">Accepted</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="CANCELLED">Cancelled</option>
                    </select>
                </div>
            </div>

            {/* Jobs Table */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center p-32 text-slate-300">
                        <Loader2 className="w-12 h-12 animate-spin mb-4" />
                        <span className="text-[10px] font-black uppercase tracking-[0.3em]">Loading Jobs...</span>
                    </div>
                ) : filteredJobs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-32 text-slate-300">
                        <XCircle className="w-12 h-12 mb-4" />
                        <span className="text-[10px] font-black uppercase tracking-[0.3em]">No Jobs Found</span>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 border-b text-slate-400 uppercase font-black text-[9px] tracking-widest">
                                <tr>
                                    <th className="px-6 py-4">Job ID</th>
                                    <th className="px-6 py-4">Customer</th>
                                    <th className="px-6 py-4">Worker</th>
                                    <th className="px-6 py-4">Skill</th>
                                    <th className="px-6 py-4">Price</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {filteredJobs.map((job) => (
                                    <tr key={job.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <p className="font-mono text-xs font-bold text-blue-600">{job.id?.substring(0, 8)}...</p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div>
                                                <p className="font-bold text-slate-900">{job.customerName || 'N/A'}</p>
                                                <p className="text-[9px] text-slate-400 font-mono mt-0.5">ID: {job.customerId?.substring(0, 8)}...</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div>
                                                <p className="font-bold text-slate-900">{job.workerName || 'Unassigned'}</p>
                                                {job.workerId && (
                                                    <p className="text-[9px] text-slate-400 font-mono mt-0.5">ID: {job.workerId?.substring(0, 8)}...</p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="bg-blue-50 text-blue-600 px-2 py-1 rounded-lg text-[9px] font-black uppercase">
                                                {job.skill}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="font-black text-slate-900">₹{job.price}</p>
                                        </td>
                                        <td className="px-6 py-4">
                                            {getStatusBadge(job.status)}
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-xs text-slate-600">
                                                {new Date(job.createdAt).toLocaleDateString('en-IN')}
                                            </p>
                                            <p className="text-[9px] text-slate-400">
                                                {new Date(job.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};
